import React, { useRef, useState } from 'react'
import apiService from '../../services/api/apiService'
import general from '../../utils/general'
import './index.scss'
import useI18n from '../../i18n/useI18n'

const FileUpload = ({
  uploadUrl,
  onUploadSuccess,
  onUploadError,
  onRemove,
  accept = '.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx',
  maxSize = 10, // MB
  label,
  showPreview = true,
  fileFieldName = 'file',
  disabled = false,
  currentFileUrl = null,
  fileType = 'auto', // 'auto', 'image', 'document'
  onFileSelect = null
}) => {
  const fileInputRef = useRef(null)
  const { t } = useI18n()
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  // Determine if file is image based on type or fileType prop
  const isImageFile = (file) => {
    if (fileType === 'image') return true
    if (fileType === 'document') return false
    return file.type.startsWith('image/')
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (disabled) return

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = (file) => {
    setError(null)

    // Check file size (MB)
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > maxSize) {
      const errorMsg = t('FILE_UPLOAD_ERROR_SIZE', { size: maxSize, defaultValue: `Dosya boyutu çok büyük. Maksimum dosya boyutu: ${maxSize} MB` })
      setError(errorMsg)
      general.notificationError(errorMsg)
      return
    }

    // Check file type
    const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase())
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
    const fileType = file.type.toLowerCase()

    const isAccepted = acceptedTypes.some(type => {
      const normalizedType = type.startsWith('.') ? type : '.' + type
      return fileExtension === normalizedType || fileType.includes(type.replace('.', ''))
    })

    if (!isAccepted) {
      const errorMsg = t('FILE_UPLOAD_ERROR_TYPE', { types: accept, defaultValue: `Geçersiz dosya tipi. Kabul edilen formatlar: ${accept}` })
      setError(errorMsg)
      general.notificationError(errorMsg)
      return
    }

    setSelectedFile(file)
    if (onFileSelect) {
      onFileSelect(file)
    }

    // Create preview for images
    if (isImageFile(file)) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result)
      }
      reader.readAsDataURL(file)
    } else {
      setPreviewUrl(null)
    }

    // Auto upload if uploadUrl is provided
    if (uploadUrl) {
      uploadFile(file)
    }
  }

  const uploadFile = async (fileToUpload = selectedFile) => {
    if (!fileToUpload || !uploadUrl) return

    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append(fileFieldName, fileToUpload)

      const response = await apiService.makeApiCall({
        url: uploadUrl,
        method: 'POST',
        axiosOptions: {
          data: formData
        }
      })

      if (response.success) {
        const uploadedFileData = response.data?.data || response.data
        general.messageSuccess(t('FILE_UPLOAD_SUCCESS', 'Dosya başarıyla yüklendi'))

        if (onUploadSuccess) {
          onUploadSuccess(uploadedFileData, fileToUpload)
        }
      } else {
        const errorMsg = response.errorMessage || t('FILE_UPLOAD_ERROR_GENERIC', 'Dosya yüklenirken bir hata oluştu')
        setError(errorMsg)
        general.notificationError(errorMsg)

        if (onUploadError) {
          onUploadError(response, fileToUpload)
        }
      }
    } catch (error) {
      const errorMsg = t('FILE_UPLOAD_ERROR_GENERIC', 'Dosya yüklenirken bir hata oluştu')
      setError(errorMsg)
      general.notificationError(errorMsg)

      if (onUploadError) {
        onUploadError(error, fileToUpload)
      }
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = (e) => {
    e.preventDefault()
    e.stopPropagation()

    setSelectedFile(null)
    setPreviewUrl(null)
    setError(null)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    // Notify parent component about removal
    if (onRemove) {
      onRemove()
    }

    if (onFileSelect) {
      onFileSelect(null)
    }

    // Also call onUploadSuccess with null for backward compatibility
    if (onUploadSuccess) {
      onUploadSuccess(null, null)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getAbsoluteUrl = (url) => {
    if (!url || typeof url !== 'string') return url;

    // 1. Önce olası baştaki/sondaki boşlukları temizleyelim
    let cleanedUrl = url.trim();

    // 2. EĞER HATA VARSA DÜZELT: "https:/" ile başlayıp "https://" ile BAŞLAMIYORSA (Tek slash hatası)
    if (cleanedUrl.startsWith('https:/') && !cleanedUrl.startsWith('https://')) {
      cleanedUrl = cleanedUrl.replace('https:/', 'https://');
    }
    // Aynı hatanın http için de geçerli olma ihtimaline karşı:
    if (cleanedUrl.startsWith('http:/') && !cleanedUrl.startsWith('http://')) {
      cleanedUrl = cleanedUrl.replace('http:/', 'http://');
    }

    // 3. Sizin mevcut geçerlilik kontrolleriniz
    if (cleanedUrl.startsWith('http://') ||
      cleanedUrl.startsWith('https://') ||
      cleanedUrl.startsWith('/') ||
      cleanedUrl.startsWith('data:') ||
      cleanedUrl.startsWith('blob:')) {
      return cleanedUrl;
    }
    // 4. Eğer hiçbir protokol yoksa (örn: "student-diplomas.com") başına ekle
    return `https://${cleanedUrl}`;
  };

  // Kullanırken trim işlemini zaten fonksiyon içinde (cleanedUrl) yaptığımız için direkt verebilirsiniz:
  const safeCurrentFileUrl = getAbsoluteUrl(currentFileUrl);
  const displayUrl = previewUrl || safeCurrentFileUrl;
  const hasFile = selectedFile || (currentFileUrl && currentFileUrl.trim())

  return (
    <div className="file-upload-wrapper">
      {(label || label === undefined) && (
        <label className="file-upload-label">{label || t('FILE_UPLOAD_LABEL_DEFAULT', 'Dosya Yükle')}</label>
      )}

      <div
        className={`file-upload-dropzone ${dragActive ? 'drag-active' : ''} ${hasFile ? 'has-file' : ''} ${disabled ? 'disabled' : ''} ${error ? 'has-error' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={(e) => {
          // Don't trigger file input if clicking on remove button or file info
          if (e.target.closest('.btn-remove-file') || e.target.closest('.file-info') || e.target.closest('.file-preview')) {
            return
          }
          if (!disabled && !uploading) {
            fileInputRef.current?.click()
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInput}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />

        {uploading ? (
          <div className="uploading-state">
            <i className="zmdi zmdi-refresh zmdi-hc-spin"></i>
            <p className="uploading-text">{t('COMMON_LOADING', 'Yükleniyor...')}</p>
          </div>
        ) : hasFile && displayUrl && isImageFile(selectedFile || { type: 'image' }) && showPreview ? (
          <div className="file-preview">
            <img src={displayUrl} alt="Preview" className="preview-image" />
            {!disabled && (
              <button
                type="button"
                className="btn-remove-file"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleRemove(e)
                }}
                title={t('FILE_UPLOAD_REMOVE', 'Dosyayı kaldır')}
              >
                <i className="zmdi zmdi-close"></i>
              </button>
            )}
            {selectedFile && (
              <div className="file-info-overlay">
                <span className="file-name">{selectedFile.name}</span>
                <span className="file-size">{formatFileSize(selectedFile.size)}</span>
              </div>
            )}
          </div>
        ) : hasFile ? (
          <div className="file-info">
            <i className={`zmdi ${isImageFile(selectedFile || { type: 'image' }) ? 'zmdi-image' : 'zmdi-file'}`}></i>
            <div className="file-details">
              {currentFileUrl && !selectedFile ? (
                <a
                  href={safeCurrentFileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="file-name-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="file-name">{t('COMMON_VIEW', 'Görüntüle')}</span>
                  <i className="zmdi zmdi-open-in-new m-l-5" style={{ fontSize: '14px' }}></i>
                </a>
              ) : (
                <span className="file-name">{selectedFile?.name || t('FILE_UPLOAD_FILE_UPLOADED', 'Dosya yüklendi')}</span>
              )}
              {selectedFile && (
                <span className="file-size">{formatFileSize(selectedFile.size)}</span>
              )}
            </div>
            {!disabled && (
              <button
                type="button"
                className="btn-remove-file"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleRemove(e)
                }}
                title={t('FILE_UPLOAD_REMOVE', 'Dosyayı kaldır')}
              >
                <i className="zmdi zmdi-close"></i>
              </button>
            )}
          </div>
        ) : (
          <>
            <i className="zmdi zmdi-cloud-upload"></i>
            <p className="dropzone-text">
              {t('FILE_UPLOAD_DROP_TEXT_PREFIX', 'Dosyayı buraya sürükleyin veya')} <span className="select-files-link">{t('FILE_UPLOAD_DROP_TEXT_SUFFIX', 'dosya seçin')}</span>
            </p>
            <p className="dropzone-hint">
              {t('FILE_UPLOAD_MAX_SIZE', { size: maxSize })} MB
              {accept && ` • ${t('FILE_UPLOAD_ACCEPTED_FORMATS', { formats: accept })}`}
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="file-upload-error">
          <i className="zmdi zmdi-alert-circle"></i>
          <span>{error}</span>
        </div>
      )}

      {selectedFile && !uploadUrl && (
        <button
          type="button"
          className="btn btn-primary btn-sm btn-round m-t-10"
          onClick={() => uploadFile()}
          disabled={uploading || disabled}
        >
          {uploading ? (
            <>
              <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
              {t('COMMON_LOADING', 'Yükleniyor...')}
            </>
          ) : (
            <>
              <i className="zmdi zmdi-cloud-upload m-r-5"></i>
              {t('FILE_UPLOAD_ACTION', 'Yükle')}
            </>
          )}
        </button>
      )}
    </div>
  )
}

export default FileUpload

